// Cognito managed login with the authorization-code flow + PKCE. No SDK: just the OAuth endpoints.
// Tokens live in sessionStorage (cleared when the tab closes); the refresh token renews them quietly.

export interface AppConfig {
  region: string;
  userPoolId: string;
  clientId: string;
  loginDomain: string;
  stage: string;
}
interface Tokens {
  idToken: string;
  accessToken: string;
  refreshToken?: string;
  expiresAt: number;
}

let cfg: AppConfig | null = null;
const TOKENS = "aisdr-auth";
const PENDING = "aisdr-auth-pending";
const redirectUri = () => `${location.origin}/`;

const safe = <T,>(fn: () => T, fallback: T): T => {
  try {
    return fn();
  } catch {
    return fallback;
  }
};

export async function loadConfig(): Promise<AppConfig> {
  if (cfg) return cfg;
  const r = await fetch("/config.json", { cache: "no-store" });
  if (!r.ok) throw new Error("config.json is missing");
  cfg = (await r.json()) as AppConfig;
  return cfg;
}

const read = (): Tokens | null => safe(() => JSON.parse(sessionStorage.getItem(TOKENS) ?? "null"), null);
const write = (t: Tokens | null) => safe(() => (t ? sessionStorage.setItem(TOKENS, JSON.stringify(t)) : sessionStorage.removeItem(TOKENS)), undefined);

const b64url = (b: Uint8Array) => btoa(String.fromCharCode(...b)).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
const random = (n = 32) => b64url(crypto.getRandomValues(new Uint8Array(n)));
const challenge = async (v: string) => b64url(new Uint8Array(await crypto.subtle.digest("SHA-256", new TextEncoder().encode(v))));

async function token(body: Record<string, string>) {
  const res = await fetch(`https://${cfg!.loginDomain}/oauth2/token`, {
    method: "POST",
    headers: { "content-type": "application/x-www-form-urlencoded" },
    body: new URLSearchParams({ client_id: cfg!.clientId, ...body }),
  });
  const d = await res.json().catch(() => ({}));
  if (!res.ok) throw Object.assign(new Error(d.error_description ?? d.error ?? "Sign-in failed"), { code: d.error });
  return d;
}
const toTokens = (d: any, keep?: string): Tokens => ({ idToken: d.id_token, accessToken: d.access_token, refreshToken: d.refresh_token ?? keep, expiresAt: Date.now() + Number(d.expires_in ?? 3600) * 1000 });

export async function signIn(opts: { signUp?: boolean } = {}) {
  const verifier = random(48);
  const state = random(12);
  safe(() => localStorage.setItem(PENDING, JSON.stringify({ verifier, state, back: location.pathname + location.search })), undefined);
  const q = new URLSearchParams({ client_id: cfg!.clientId, response_type: "code", scope: "openid email profile", redirect_uri: redirectUri(), state, code_challenge: await challenge(verifier), code_challenge_method: "S256" });
  location.assign(`https://${cfg!.loginDomain}/${opts.signUp ? "signup" : "oauth2/authorize"}?${q}`);
}

/** Call on load: finishes a sign-in return (?code=…&state=…). Returns an error message or null. */
export async function completeSignIn(): Promise<string | null> {
  const p = new URLSearchParams(location.search);
  if (!p.has("code") && !p.has("error")) return null;
  const pending = safe(() => JSON.parse(localStorage.getItem(PENDING) ?? "null"), null) as { verifier: string; state: string; back: string } | null;
  safe(() => localStorage.removeItem(PENDING), undefined);
  history.replaceState(null, "", pending?.back ?? "/");
  if (p.has("error")) return p.get("error_description") ?? "Sign-in was cancelled";
  if (!pending || pending.state !== p.get("state")) return "That sign-in link expired. Please sign in again.";
  try {
    write(toTokens(await token({ grant_type: "authorization_code", code: p.get("code")!, redirect_uri: redirectUri(), code_verifier: pending.verifier })));
    return null;
  } catch (e) {
    return (e as Error).message;
  }
}

let refreshing: Promise<Tokens | null> | null = null;
export async function idToken(): Promise<string | null> {
  let t = read();
  if (!t) return null;
  if (t.expiresAt - Date.now() < 60_000) {
    const cur = t;
    refreshing ??= (cur.refreshToken ? token({ grant_type: "refresh_token", refresh_token: cur.refreshToken }).then((d) => toTokens(d, cur.refreshToken)) : Promise.resolve(null))
      .catch(() => null)
      .then((n) => {
        write(n);
        return n;
      })
      .finally(() => (refreshing = null));
    t = await refreshing;
  }
  return t?.idToken ?? null;
}

export function claims(): Record<string, any> | null {
  const t = read();
  if (!t) return null;
  return safe(() => JSON.parse(decodeURIComponent(escape(atob(t.idToken.split(".")[1]!.replace(/-/g, "+").replace(/_/g, "/"))))), null);
}

export async function signOut() {
  const t = read();
  write(null);
  if (t?.refreshToken)
    await fetch(`https://${cfg!.loginDomain}/oauth2/revoke`, { method: "POST", headers: { "content-type": "application/x-www-form-urlencoded" }, body: new URLSearchParams({ token: t.refreshToken, client_id: cfg!.clientId }) }).catch(() => undefined);
  location.assign(`https://${cfg!.loginDomain}/logout?${new URLSearchParams({ client_id: cfg!.clientId, logout_uri: redirectUri() })}`);
}
